import React, { FunctionComponent } from "react";
import {
  Card,
  CardContent,
  Stack,
  Box,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { CustomInput } from "../../components/CustomInput";
import { Button } from "../../components/Button";
import { useEditProfileForm } from "./Hook";
import { User } from "../../model/User";
import { IProps } from "./IProps";
import { CustomSnackbar } from "../CustomSnackbar";
import { CustomTabs } from "../CustomTabs";
import { ActiveProspect } from "./components/ActiveProspect";
import { CompanyInfo } from "./components/CompanyInfo";
import { HacthSection } from "./components/Hatch";
import { I360Section } from "./components/I360";
import { LeadPerfectionData } from "./components/LeadPerfection";
import { MarketSharpSection } from "./components/MarketSharp";
import { Marlimar } from "./components/Marlimar";
import { Zapier } from "./components/Zapier";
import { NewInputDialog } from "./components/NewInputDialog";

export const EditProfileForm: FunctionComponent<IProps> = (
  props
): JSX.Element => {
  const user = new User();
  const disableReadForm = user.HasForbiddenAccess("Can Read User Info");
  const disableEditForm = user.HasForbiddenAccess("Can Edit User Info");

  const {
    activeProspect,
    companyInfo,
    secLevel,
    hatch,
    i360,
    sectionFields,
    leadPerfection,
    marketSharp,
    marlimar,
    zapier,
    isLoading,
    setActiveProspect,
    setCompanyInfo,
    setHatch,
    setI360,
    setLeadPerfection,
    setMarketSharp,
    setMarlimar,
    setZapier,
    snackbarLevel,
    snackbarMessage,
    snackbarOpen,
    onCloseSnackbar,
    onUpdateUserProfile,
    onAddField,
    onChangeField,
    toObject,
    onPauseLeads,
    pauseLoading,
  } = useEditProfileForm(props.username);

  const disableFieldsForDealers = (): boolean => {
    return user.Roles[0].secLevel === 1;
  };

  return (
    <>
      <Backdrop open={isLoading} sx={{ zIndex: 1 }}>
        <CircularProgress />
      </Backdrop>
      <Card sx={{ boxShadow: 2, mt: 2, borderRadius: 4 }}>
        <CardContent sx={{ p: 0.4 }}>
          {!props.isIntegrationsEnabled ? (
            <CompanyInfo
              fields={[]}
              pauseLoading={pauseLoading}
              onPauseLead={onPauseLeads}
              onChangeField={() => null}
              onAdd={() => null}
              disableFieldsForDealers={disableFieldsForDealers}
              disableReadForm={disableReadForm}
              data={companyInfo}
              isDealer={secLevel === 1}
              onChangeCheckbox={(event) => {
                setCompanyInfo((prevState) => ({
                  ...prevState,
                  sendEmail: event.target.checked,
                }));
              }}
              onChange={(event) => {
                setCompanyInfo((prevState) => ({
                  ...prevState,
                  [event.target.name]: event.target.value,
                }));
              }}
            />
          ) : (
            <>
              <CustomTabs
                tabLabels={[
                  {
                    title: "LeadPerfection",
                  },
                  {
                    title: "Market Sharp",
                  },
                  {
                    title: "i360",
                  },
                  {
                    title: "Hatch",
                  },
                  {
                    title: "Active Prospect",
                  },
                  {
                    title: "Zapier",
                  },
                  {
                    title: "Marlimar",
                  },
                ]}
                tabComponents={[
                  <LeadPerfectionData
                    data={leadPerfection}
                    onChange={(event) => {
                      setLeadPerfection((prevState) => ({
                        ...prevState,
                        [event.target.name]: event.target.value,
                      }));
                    }}
                    fields={sectionFields.leadPerfection}
                    onChangeField={(event, index) =>
                      onChangeField(event, "leadPerfection", index)
                    }
                    onAdd={(name, value) =>
                      onAddField("leadPerfection", name, value)
                    }
                  />,
                  <MarketSharpSection
                    data={marketSharp}
                    fields={sectionFields.marketSharp}
                    onChangeField={(event, index) =>
                      onChangeField(event, "marketSharp", index)
                    }
                    onAdd={(name, value) =>
                      onAddField("marketSharp", name, value)
                    }
                    onChange={(event) => {
                      setMarketSharp((prevState) => ({
                        ...prevState,
                        [event.target.name]: event.target.value,
                      }));
                    }}
                  />,
                  <I360Section
                    data={i360}
                    fields={sectionFields.i360}
                    onChangeField={(event, index) =>
                      onChangeField(event, "i360", index)
                    }
                    onAdd={(name, value) => onAddField("i360", name, value)}
                    onChange={(event) => {
                      setI360((prevState) => ({
                        ...prevState,
                        [event.target.name]: event.target.value,
                      }));
                    }}
                  />,
                  <HacthSection
                    data={hatch}
                    fields={sectionFields.hatch}
                    onChangeField={(event, index) =>
                      onChangeField(event, "hatch", index)
                    }
                    onAdd={(name, value) => onAddField("hatch", name, value)}
                    onChange={(event) => {
                      setHatch((prevState) => ({
                        ...prevState,
                        [event.target.name]: event.target.value,
                      }));
                    }}
                  />,
                  <ActiveProspect
                    data={activeProspect}
                    fields={sectionFields.activeProspect}
                    onChangeField={(event, index) =>
                      onChangeField(event, "activeProspect", index)
                    }
                    onAdd={(name, value) =>
                      onAddField("activeProspect", name, value)
                    }
                    onChange={(event) => {
                      setActiveProspect((prevState) => ({
                        ...prevState,
                        [event.target.name]: event.target.value,
                      }));
                    }}
                  />,
                  <Zapier
                    data={zapier}
                    fields={sectionFields.zapier}
                    onChangeField={(event, index) =>
                      onChangeField(event, "zapier", index)
                    }
                    onAdd={(name, value) => onAddField("zapier", name, value)}
                    onChange={(event) => {
                      setZapier((prevState) => ({
                        ...prevState,
                        [event.target.name]: event.target.value,
                      }));
                    }}
                  />,
                  <Marlimar
                    fields={sectionFields.marlimar}
                    onChangeField={(event, index) =>
                      onChangeField(event, "marlimar", index)
                    }
                    onAdd={(name, value) => onAddField("marlimar", name, value)}
                    data={marlimar}
                    onChange={(event) => {
                      setMarlimar((prevState) => ({
                        ...prevState,
                        [event.target.name]: event.target.value,
                      }));
                    }}
                  />,
                ]}
                orientation="vertical"
              />
            </>
          )}
        </CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%",
            borderTop: 1,
            backgroundColor: "#FAFAFA",
            borderColor: "#00000026",
            p: 2,
          }}
        >
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" width={120} height={40} label="Cancel" />
            <Button
              onClick={onUpdateUserProfile}
              disabled={disableReadForm || disableEditForm}
              width={120}
              height={40}
              label="Save"
            />
          </Stack>
        </Box>
        <CustomSnackbar
          open={snackbarOpen}
          onClose={onCloseSnackbar}
          message={snackbarMessage}
          level={snackbarLevel as any}
          variant="secondary"
        />
      </Card>
    </>
  );
};
